import {
  IconAppleOutline18,
  IconAtomOutline18,
  IconBookOutline18,
  IconBracketsCurlyOutline18,
  IconClockOutline18,
  IconCodeOutline18,
  IconCubeOutline18,
  IconGridLayoutOutline18,
  IconLaptopMobileOutline18,
  IconLayersOutline18,
  IconMapOutline18,
  IconMobileOutline18,
  IconPlugOutline18,
  IconRocketOutline18,
  IconRouteOutline18,
  IconShapesOutline18,
} from "nucleo-ui-outline-18";

import type { Icon } from "@mountayaapp/theme";

/**
 * @mountayaapp/docs curated navigation. The header tabs, sidebar order, grouping,
 * and icons are an explicit, hand-curated tree here rather than derived from
 * per-page frontmatter, so the information architecture reads the same regardless
 * of how pages were authored. Frontmatter still owns each page's `title` and
 * `description`; this module owns the shape and sequence of the chrome.
 */

/**
 * TabId names a top-level header tab. The header renders the tabs in `TABS`
 * order; the registry resolves a pathname to its owning tab; the shell reads the
 * tab's layout to decide whether a sidebar shows. A single union keeps the id set
 * closed so a typo cannot introduce a tab no config knows about.
 */
export type TabId = "documentation" | "apis" | "sdks" | "tools" | "changelog";

/**
 * TabLayout is how a tab frames its pages. `sidebar` draws the curated group rail
 * plus the page chrome (breadcrumbs, pager, table of contents); `bare` is a
 * full-width column with none of that chrome, used by the interactive explorers
 * and the changelog.
 */
export type TabLayout = "sidebar" | "bare";

/**
 * NavSubGroup is a section nested one level inside a group: a localized `labelKey` heading
 * over the ordered `slugs` it contains.
 *
 * The heading is a LABEL rather than a page, and it is the only shape this takes. It used to
 * have a second arm whose heading was itself a page, for a rail organized by npm package
 * where a package's overview headed its own pages. A task-shaped rail has no such pairing:
 * a group is a job, the pages under it are peers, and a section exists only to break a long
 * peer list into readable runs. Nothing documents "the map recipes" on its own, so nothing
 * to link the heading to.
 *
 * One level, deliberately. A rail that nests twice asks a reader to open two disclosures to
 * reach a page, which costs more than the grouping saves.
 */
export interface NavSubGroup {
  readonly labelKey: string;
  readonly slugs: readonly string[];
}

/** One entry in a group: a page, or a nested section of pages. */
export type NavEntry = string | NavSubGroup;

/**
 * NavGroup is one collapsible section of the sidebar: the `tab` it belongs to, a
 * localized `labelKey` resolved against the `docs` namespace, a Nucleo `icon`
 * shown beside the heading, and the ordered, language-agnostic `entries` it
 * contains. An entry is a slug — the trailing-slash-free path a page is served at within a
 * language (`""` is the language root / overview) — or a {@link NavSubGroup} of them. The
 * registry resolves each slug to a page in the active language, falling back to English, and
 * drops slugs with no page so the rendered sidebar never carries a dead link. The `tab` tag is
 * what lets the sidebar show only the active tab's groups.
 */
export interface NavGroup {
  icon: Icon;
  /**
   * The {@link TabItem} this group belongs to, for a tab whose destinations are a menu.
   *
   * The SDKs tab holds one item per language family, and each family publishes its own
   * packages. Without this the rail would show every language's packages at once, which is
   * the list a reader opened one language to avoid. A group with no `item` belongs to its
   * whole tab, which is every group on a tab that navigates rather than opening a menu.
   */
  item?: string;
  labelKey: string;
  entries: readonly NavEntry[];
  tab: TabId;
}

/**
 * TabItem is one destination inside a dropdown tab: its `id` (what a {@link NavGroup}
 * names to claim it), the localized `labelKey`, the Nucleo `icon` beside it, and either
 * the `slug` it navigates to or `planned: true`.
 *
 * Two arms, because a destination either exists or is on the way. A planned item carries
 * NO slug at all rather than a slug and a flag: there is no page to point at, and a shape
 * that could hold one is a shape somebody will fill in with a guess.
 */
export type TabItem =
  | { readonly icon: Icon; readonly id: string; readonly labelKey: string; readonly slug: string }
  | { readonly icon: Icon; readonly id: string; readonly labelKey: string; readonly planned: true };

/**
 * TabBase is what every header tab carries: its `id`, the localized `labelKey` for its
 * label, the Nucleo `icon` beside that label, an `owns` predicate that decides which slugs
 * route to this tab (for active-state and layout), and the `layout` it frames its pages
 * with.
 */
interface TabBase {
  icon: Icon;
  id: TabId;
  labelKey: string;
  layout: TabLayout;
  owns: (slug: string) => boolean;
}

/**
 * Tab is one header tab, and it is a closed union: a tab either NAVIGATES to a landing page
 * or OPENS a menu of destinations, never both. A trigger that did both is unreachable on touch — the tap that would open the menu
 * is the tap that follows the link — so the two are separate shapes rather than one shape
 * with an optional `items`.
 *
 * `TABS` order is reading order left-to-right in the header's second row.
 */
export type Tab = (TabBase & { landingSlug: string }) | (TabBase & { items: readonly TabItem[] });

/**
 * NAVIGATION is the ordered sidebar tree across every tab. Group order and the
 * slug order within each group are the reading order of that section; the registry
 * preserves both when it resolves slugs to pages and filters groups to the active
 * tab.
 *
 * **No generated reference appears here.** The Data API explorer and the two Redoc
 * references are each a full-width schema browser that carries its own navigation, so a
 * rail entry offered a link that left the rail behind — and browsing the guides kept
 * landing a reader in a schema browser. They are the API references tab's own menu now;
 * each surface's overview keeps a prose link to its reference, which is a cross-link
 * rather than a step in a reading order. `isReachableSlug` knows this, and the discovery
 * files gather them in their own trailing section.
 *
 * Nor does a page reached through a page-level tab strip: a Props or Styling tab, and the
 * Map Publishing parameter reference. Those are tabs OF a guide rather than pages beside
 * it, so keeping them out of this tree is what keeps them out of the pager's reading
 * order, the breadcrumb trail and the group manifest, all at once.
 *
 * The SDK groups are named after jobs rather than after npm packages. A reader who wants
 * an elevation profile looks for the itinerary group, not for the package the component
 * happens to ship in; the package name belongs in the install snippet, which is where the
 * install page keeps it. Guides come first in the order a reader meets them — get the keys,
 * fetch data, draw a map, read itinerary data, build a studio — then the two flat reference
 * lists, then the example gallery as a peer of the guides rather than a page inside one.
 *
 * A group is declared here before its pages exist, and the chrome handles the gap
 * on its own: until a page resolves under a slug the registry drops that item, and
 * a group whose slugs all resolve to nothing loses its heading while the header
 * loses its tab. Authoring `content/<lang>/<section>/**` is the only step that
 * turns a section on.
 */
export const NAVIGATION: readonly NavGroup[] = [
  {
    entries: ["", "quickstart", "authentication", "concepts", "requests", "errors"],
    icon: IconRocketOutline18,
    labelKey: "nav.group.startHere",
    tab: "documentation",
  },
  {
    entries: [
      "map-publishing",
      "map-publishing/quickstart",
      "map-publishing/events",
      "map-publishing/examples",
    ],
    icon: IconMapOutline18,
    labelKey: "nav.group.mapPublishing",
    tab: "documentation",
  },
  {
    entries: ["map-studio-api", "map-studio-api/quickstart"],
    icon: IconCodeOutline18,
    labelKey: "nav.group.mapStudioApi",
    tab: "documentation",
  },
  {
    entries: [
      "data-api",
      "data-api/quickstart",
      "data-api/routing",
      "data-api/places",
      "data-api/itineraries",
      "data-api/analysis",
      "data-api/terrain",
    ],
    icon: IconRouteOutline18,
    labelKey: "nav.group.dataApi",
    tab: "documentation",
  },
  {
    entries: ["tile-api", "tile-api/quickstart", "tile-api/overlays", "tile-api/integration"],
    icon: IconLayersOutline18,
    labelKey: "nav.group.tileApi",
    tab: "documentation",
  },
  {
    // The client sits here rather than in a group of its own: fetching is the first thing
    // a reader does after installing, and one page under its own heading was a heading
    // that existed to hold a single link.
    entries: ["sdks/typescript", "sdks/typescript/install", "sdks/typescript/client"],
    icon: IconRocketOutline18,
    item: "typescript",
    labelKey: "nav.group.sdkGetStarted",
    tab: "sdks",
  },
  {
    entries: [
      "sdks/typescript/map",
      "sdks/typescript/editor",
      "sdks/typescript/customize",
      "sdks/typescript/without-react",
    ],
    icon: IconMapOutline18,
    item: "typescript",
    labelKey: "nav.group.sdkShowMap",
    tab: "sdks",
  },
  {
    entries: ["sdks/typescript/elevation-profile", "sdks/typescript/tables"],
    icon: IconRouteOutline18,
    item: "typescript",
    labelKey: "nav.group.sdkItineraryData",
    tab: "sdks",
  },
  {
    entries: [
      "sdks/typescript/studio",
      "sdks/typescript/studio/headless",
      "sdks/typescript/studio/chrome",
      "sdks/typescript/studio/collaboration",
      "sdks/typescript/studio/video",
    ],
    icon: IconGridLayoutOutline18,
    item: "typescript",
    labelKey: "nav.group.sdkBuildStudio",
    tab: "sdks",
  },
  {
    entries: ["sdks/typescript/hooks", "sdks/typescript/handlers", "sdks/typescript/testing"],
    icon: IconBookOutline18,
    item: "typescript",
    labelKey: "nav.group.sdkReference",
    tab: "sdks",
  },
  {
    // A peer of the guides rather than a page inside one. A recipe answers "show me the code
    // for this" and a guide answers "how does this work", and a reader arrives already
    // knowing which of the two they want.
    //
    // Nineteen recipes in one flat list is a list nobody reads to the end, so they sit in
    // three sections headed by the job they belong to. Those headings are labels rather than
    // pages: nothing documents "the map recipes" on its own, and a heading that linked to the
    // gallery index would be a second route to the entry directly above it.
    entries: [
      "sdks/typescript/examples",
      {
        labelKey: "nav.group.exampleMap",
        slugs: [
          "sdks/typescript/examples/map",
          "sdks/typescript/examples/custom-style",
          "sdks/typescript/examples/map-controls",
          "sdks/typescript/examples/search",
          "sdks/typescript/examples/street-view",
          "sdks/typescript/examples/terrain-switcher",
          "sdks/typescript/examples/preferences",
        ],
      },
      {
        labelKey: "nav.group.exampleItinerary",
        slugs: [
          "sdks/typescript/examples/elevation-profile",
          "sdks/typescript/examples/profile-with-map",
          "sdks/typescript/examples/segment-table",
          "sdks/typescript/examples/segment-table-with-map",
          "sdks/typescript/examples/segment-list",
          "sdks/typescript/examples/waypoint-table",
          "sdks/typescript/examples/race-course",
          "sdks/typescript/examples/waypoint-list",
          "sdks/typescript/examples/waypoint-card",
          "sdks/typescript/examples/custom-waypoint-card",
        ],
      },
      {
        labelKey: "nav.group.exampleStudio",
        slugs: ["sdks/typescript/examples/studio-chrome", "sdks/typescript/examples/full-studio"],
      },
    ],
    icon: IconShapesOutline18,
    item: "typescript",
    labelKey: "nav.group.sdkExamples",
    tab: "sdks",
  },
  {
    entries: ["tools", "tools/mcp", "tools/ai-agents"],
    icon: IconPlugOutline18,
    labelKey: "nav.group.tools",
    tab: "tools",
  },
  {
    entries: ["changelog"],
    icon: IconClockOutline18,
    labelKey: "nav.group.changelog",
    tab: "changelog",
  },
];

/**
 * groupSlugs is every slug a group lists, nested sections flattened into reading order.
 *
 * The rail nests; the reading order does not. A pager stepping through a section, a breadcrumb
 * naming the section a page sits in, and the orphan check all want the flat sequence, and
 * deriving it here rather than in each of them is what stops a nested page from being the one
 * the pager skips.
 */
export const groupSlugs = (group: NavGroup): string[] =>
  group.entries.flatMap((entry) => {
    if (typeof entry === "string") {
      return [entry];
    }

    return [...entry.slugs];
  });

/**
 * ownsSection builds an `owns` predicate over a section's root slugs: a slug
 * belongs to the tab when it is one of those roots or sits beneath it. Ownership
 * is by prefix rather than an explicit slug list so a page authored inside an
 * existing section routes to its tab without being registered twice.
 */
const ownsSection =
  (roots: readonly string[]) =>
  (slug: string): boolean =>
    roots.some((root) => slug === root || slug.startsWith(`${root}/`));

/**
 * DOCUMENTATION_TAB is the fallback tab every unclaimed slug routes to: its `owns`
 * answers true for everything, so a slug no other tab claims (the guides, the
 * landing page) belongs to it. It is named on its own, then placed first in
 * `TABS`, so `resolveTabId` can return it as a guaranteed-defined reference
 * without depending on array indexing.
 */
const DOCUMENTATION_TAB: Tab = {
  icon: IconBookOutline18,
  id: "documentation",
  labelKey: "nav.tab.documentation",
  landingSlug: "",
  layout: "sidebar",
  owns: () => true,
};

/**
 * TABS is the ordered header tab configuration and the single source of truth for the tab
 * information architecture. Five destinations, in the order a reader needs them:
 * Documentation, which is the fallback and holds everything read as prose (the entry path,
 * Map Publishing, and each callable surface's guides); the SDKs that call those surfaces;
 * the AI tooling built on them; then the generated API references, which are a lookup a
 * reader drops into rather than a place they start; then the changelog.
 *
 * **Documentation is guides only.** The generated references used to be rail entries
 * inside their product's guide group, so browsing the guides kept landing a reader in a
 * schema browser — a page that carries its own navigation and leaves the rail behind.
 * They are one dropdown now, beside the guides rather than inside them, and each
 * surface's overview still links its reference in prose.
 *
 * The callable surfaces are always listed **Map Studio API, Data API, Tile API**, here
 * and in every card group and rail across the documentation. That order is the product's
 * own, not an alphabetical or size-based accident, so a reader meets the three in the
 * same sequence wherever they appear.
 *
 * SDKs is a dropdown for a different reason: the TypeScript packages are one language
 * family of four, and a flat list of npm package names had no level saying so. A second
 * language is a sibling item here rather than a rewrite of the rail. The three that do
 * not exist yet are listed as `planned`, so the roadmap is visible and unreachable.
 *
 * The three references still render edge-to-edge, through their own `full: true`
 * frontmatter: `DocsAppShell` drops the rail for a full-bleed page, so an explorer
 * that carries its own navigation never draws a second rail beside ours.
 */
export const TABS: readonly Tab[] = [
  DOCUMENTATION_TAB,
  {
    icon: IconCubeOutline18,
    id: "sdks",
    items: [
      {
        icon: IconAtomOutline18,
        id: "typescript",
        labelKey: "nav.sdk.typescript",
        slug: "sdks/typescript",
      },
      { icon: IconAppleOutline18, id: "swift", labelKey: "nav.sdk.swift", planned: true },
      {
        icon: IconLaptopMobileOutline18,
        id: "kotlin",
        labelKey: "nav.sdk.kotlin",
        planned: true,
      },
      { icon: IconMobileOutline18, id: "dart", labelKey: "nav.sdk.dart", planned: true },
    ],
    labelKey: "nav.tab.sdks",
    layout: "sidebar",
    owns: ownsSection(["sdks"]),
  },
  {
    icon: IconPlugOutline18,
    id: "tools",
    labelKey: "nav.tab.tools",
    landingSlug: "tools",
    layout: "sidebar",
    owns: ownsSection(["tools"]),
  },
  {
    icon: IconBracketsCurlyOutline18,
    id: "apis",
    items: [
      {
        icon: IconCodeOutline18,
        id: "map-studio",
        labelKey: "nav.api.mapStudio",
        slug: "api-reference/map-studio",
      },
      // The Data API's reference IS GraphiQL's own Docs pane: the schema browses itself,
      // and there is no generated per-type reference behind this item.
      {
        icon: IconRouteOutline18,
        id: "data",
        labelKey: "nav.api.data",
        slug: "api-reference/data",
      },
      {
        icon: IconLayersOutline18,
        id: "tiles",
        labelKey: "nav.api.tiles",
        slug: "api-reference/tiles",
      },
    ],
    labelKey: "nav.tab.apiReferences",
    layout: "bare",
    owns: ownsSection(["api-reference"]),
  },
  {
    icon: IconClockOutline18,
    id: "changelog",
    labelKey: "nav.tab.changelog",
    landingSlug: "changelog",
    layout: "bare",
    owns: (slug) => slug === "changelog",
  },
];

/**
 * resolveTabItem returns the destination inside a dropdown tab that a slug sits under,
 * or `null` for a slug under none of them. Matching is by prefix, the same rule
 * {@link ownsSection} uses one level up, and the LONGEST match wins so a language whose
 * slug nests inside another's still resolves to its own.
 *
 * It is what lets the rail show one language's packages rather than every language's at
 * once: the sidebar reads the active item off the pathname and narrows the manifest to it.
 */
export const resolveTabItem = (tab: Tab, slug: string): TabItem | null => {
  if (!("items" in tab)) {
    return null;
  }

  let match: TabItem | null = null;
  for (const item of tab.items) {
    if (!("slug" in item) || !(slug === item.slug || slug.startsWith(`${item.slug}/`))) {
      continue;
    }

    if (match === null || !("slug" in match) || item.slug.length > match.slug.length) {
      match = item;
    }
  }

  return match;
};

/**
 * AUTHORED_PAGE_TABS declares the page-level tabs that are written rather than derived,
 * keyed by the guide they hang off.
 *
 * A guide's Props and Styling tabs come from the element manifests, because the manifests
 * already answer which page documents which element. Nothing answers "this page is the
 * parameter reference for that one", so that pairing is curated here — like every other
 * piece of this information architecture — and merged with the derived set by
 * `listPageTabs`.
 *
 * Its one entry today is the hosted embed's parameter reference: a page whose own
 * description calls it "the full query-parameter reference", which is a tab of the Map
 * Publishing overview rather than the third step of its reading order.
 */
export const AUTHORED_PAGE_TABS: Readonly<
  Record<string, readonly { readonly labelKey: string; readonly slug: string }[]>
> = {
  "map-publishing": [{ labelKey: "page.tab.parameters", slug: "map-publishing/parameters" }],
};

/**
 * resolveTabId returns the tab that owns a slug. The non-fallback tabs are tested
 * in `TABS` order and the first whose `owns` matches wins; when none claims the
 * slug it belongs to Documentation, the catch-all. Centralizing the rule here
 * keeps the header, shell, and breadcrumbs from each re-deriving tab ownership.
 */
export const resolveTabId = (slug: string): Tab => {
  for (const tab of TABS) {
    if (tab.id !== "documentation" && tab.owns(slug)) {
      return tab;
    }
  }

  return DOCUMENTATION_TAB;
};
